/** @format */

import React, { useEffect, useRef, useState, useTransition } from "react";
import Big from "big.js";
import Swal from "sweetalert2";
import "./Swap.css";
import {
  useWeb3ModalProvider,
  useWeb3ModalAccount,
} from "@web3modal/ethers/react";
import { toast } from "react-toastify";
import { Contract, Network, ethers } from "ethers";
import {
  bnbChainId,
  chain,
  presaleAbi,
  presaleAddress,
  rpcUrl,
  token,
  tokenAbi,
  tokenAddress,
  tokenSymbol,
  usdtAbi,
  usdtAddress,
} from "./utils/constants";
import helperFunctions from "./utils/helper";
import { deci, deciB, formatNumber, presaleStates } from "./utils/utils.js";
import { useTranslation } from "react-i18next";
function Swap() {
  const { t } = useTranslation();
  const { address, chainId, isConnected } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();

  const [presaleContract, setPresaleContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [saleTokenContract, setSaleTokenContract] = useState(null);

  const [totalTime, setTotalTime] = useState(0);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [roundRate, setRoundRate] = useState(0);
  const [tokenPrice, setTokenPrice] = useState(0);

  const [bnbBalance, setBNBBalance] = useState(0);
  const [usdtBalance, setUSDTBalance] = useState(0);

  const [userBalance, setUserBalance] = useState({
    tokenForSale: deci(0),
  });

  const [totalTokensSold, setTotalTokensSold] = useState(0);
  const [tokensInPresale, setTokensInPresale] = useState(0);
  const [availableForPurchase, setAvailableForPurchase] = useState(0);
  const [bnbSelected, setBnbSelected] = useState(true);
  const [timeText, setTimeText] = useState("");

  const [saleState, setSaleState] = useState(presaleStates.IN_FUTURE);
  const [saleStateText, setSaleStateText] = useState("");
  const [amountUsdtPay, setAmountUsdtPay] = useState(1);
  const [amountBnbPay, setAmountBnbPay] = useState(1);
  const [amountTokenReceivedBnb, setAmountTokenReceivedBnb] = useState(0);
  const [amountTokenReceivedUsdt, setAmountTokenReceivedUsdt] = useState(0);
  const timerRef = useRef();

  const [timerData, setTimerData] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [soldPercent, setSoldPercentage] = useState(0);
  const getInitialValues = async () => {
    try {
      const network = new ethers.Network(chain.currency, chain.chainId);
      let provider = new ethers.JsonRpcProvider(rpcUrl, network, {
        staticNetwork: true,
        batchMaxCount: 1,
      });
      let _presaleContract = new Contract(presaleAddress, presaleAbi, provider);
      let _roundDetails = await _presaleContract.rounds(0);
      setRoundRate(_roundDetails[0].toString());
      let tokenPrice = parseFloat(
        (parseFloat(100000) / parseFloat(_roundDetails[0])).toFixed(8)
      );
      setTokenPrice(tokenPrice);
      let _startTime = await _presaleContract.startTime();
      setStartDate(new Date(Number(_startTime) * 1000));
      let _endTime = await _presaleContract.endTime();
      setEndDate(new Date(Number(_endTime) * 1000));
      setTotalTime(parseInt(_endTime));
      let totalTokensSold = await _presaleContract.totalTokensSold();
      setTotalTokensSold(parseInt(totalTokensSold));
      let totalTokenInPresale = await _presaleContract.totalTokenInPresale();
      setTokensInPresale(parseInt(totalTokenInPresale));
      let availableForPurchase = await _presaleContract.getTokenBalance();
      setAvailableForPurchase(parseInt(availableForPurchase));
    } catch (error) {
      console.log(error);
    }
  };
  const init = async () => {
    try {
      if (!isConnected) return;
      const provider = new ethers.BrowserProvider(walletProvider);
      setBNBBalance(parseInt(await provider.getBalance(address)));
      const signer = await provider.getSigner();
      const contract = new Contract(presaleAddress, presaleAbi, signer);
      setPresaleContract(contract);
      const _tokenContract = new Contract(usdtAddress, usdtAbi, signer);
      setTokenContract(_tokenContract);
      const _saleTokenContract = new Contract(tokenAddress, tokenAbi, provider);
      setSaleTokenContract(_saleTokenContract);
    } catch (error) {
      console.log(error);
      toast.info(t("something_went_wrong"));
    }
  };

  const getMe2DecimalPointsWithCommas = (amount) => {
    return Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };
  const dividor = (a, b) => {
    return deciB(a).div(deciB(b));
  };
  const fromWei = (amount) => {
    return deci(amount)
      .div(10 ** 18)
      .toString();
  };
  const getFormattedAmount = (amount) => {
    return getMe2DecimalPointsWithCommas(fromWei(amount));
  };
  const getSaleStateTextForTimer = (saleState) => {
    console.log(saleState);
    if (saleState === presaleStates.EXPIRED) {
      return t("expired");
    } else if (saleState === presaleStates.IN_FUTURE) {
      return t("before_sale_start");
    } else if (saleState === presaleStates.RUNNING) {
      return t("before_sale_end");
    } else if (saleState === presaleStates.SOLD_OUT) {
      return t("sold_out");
    }
  };
  const getSaleState = () => {
    let curTime = Date.now();
    let startTime = startDate.getTime();
    let endTime = endDate.getTime();

    if (deci(totalTokensSold).gte(tokensInPresale)) {
      return presaleStates.EXPIRED;
    }

    if (curTime < startTime) {
      return presaleStates.IN_FUTURE;
    } else if (curTime >= startTime && curTime < endTime) {
      return presaleStates.RUNNING;
    } else {
      console.log(curTime, startTime, endTime);
      return presaleStates.EXPIRED;
    }
  };
  const fetchDetails = async () => {
    try {
      if (!presaleContract) return;
      let _roundDetails = await presaleContract.rounds(0);
      setRoundRate(_roundDetails[0].toString());
      let _startTime = await presaleContract.startTime();
      setStartDate(new Date(Number(_startTime) * 1000));
      let _endTime = await presaleContract.endTime();
      setEndDate(new Date(Number(_endTime) * 1000));

      setTotalTime(parseInt(_endTime));

      let totalTokensSold = await presaleContract.totalTokensSold();
      setTotalTokensSold(parseInt(totalTokensSold));
      let totalTokenInPresale = await presaleContract.totalTokenInPresale();
      setTokensInPresale(parseInt(totalTokenInPresale));
      let availableForPurchase = await presaleContract.getTokenBalance();
      setAvailableForPurchase(parseInt(availableForPurchase));
      setSoldPercentage(10.04);
      let soldPrecentage =
        parseFloat(totalTokenInPresale) === 0
          ? 0
          : (
              parseFloat(totalTokensSold) / parseFloat(totalTokenInPresale)
            ).toFixed(4) * 100;
      setSoldPercentage(soldPrecentage);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUsdtBalance = async () => {
    try {
      if (!tokenContract) return;
      let balance = deci(Number(await tokenContract.balanceOf(address)));
      setUSDTBalance(balance);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSaleTokenBalance = async () => {
    try {
      if (!saleTokenContract) return;
      let balance = deciB(await saleTokenContract.balanceOf(address));
      setUserBalance({
        ...userBalance,
        tokenForSale: balance,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const maxToken = (isBnb) => {
    if (isBnb) {
      setAmountBnbPay(fromWei(bnbBalance));
    } else {
      setAmountUsdtPay(fromWei(usdtBalance));
    }
    // Calculate maximum token amount user can buy
    // Update state variables as needed
  };

  const approveTokens = async (amount) => {
    try {
      if (!tokenContract) return false;
      let res = await tokenContract.approve(presaleAddress, amount.toString());
      await res.wait();
      return true;
    } catch (error) {
      throw error;
    }
  };

  const clearInputs = () => {
    setAmountBnbPay(0);
    setAmountUsdtPay(0);
  };

  const buyWithUSDT = async () => {
    try {
      if (chainId !== bnbChainId) {
        Swal.fire(t("warning"), t("switch_chain_to_bsc"), "warning");
        return;
      }
      if (!isConnected) {
        Swal.fire(t("warning"), t("connect_wallet_first"), "warning");
        return;
      }
      let purchasableAmount = deci(availableForPurchase).div(10 ** 18);
      let receivableAmount = deci(amountTokenReceivedUsdt);

      if (receivableAmount.greaterThan(purchasableAmount)) {
        Swal.fire(
          t("warning"),
          t("purchase_exceeds_available_tokens"),
          "warning"
        );
        return;
      }

      let weiValue = deci(amountUsdtPay).mul(10 ** 18);
      if (weiValue.lessThan(0)) {
        Swal.fire(t("warning"), t("amount_greater_than_zero"), "warning");
        return;
      }
      if (deci(usdtBalance).lessThan(weiValue)) {
        Swal.fire(t("warning"), t("insufficient_balance"), "warning");
        return;
      }

      setPurchaseLoading(true);

      let allowance = Number(
        await tokenContract.allowance(address, presaleAddress)
      );

      if (deci(allowance).lessThan(weiValue)) {
        if (allowance === 0) {
          await approveTokens(weiValue.toFixed(0));
        } else {
          await approveTokens(0);
          await approveTokens(weiValue.toFixed(0));
        }
      }
      //Send Purchase Transaction
      let res = await presaleContract.buyWithUSDT(weiValue.toFixed(0));
      await res.wait();
      Swal.fire(
        t("transaction_completed"),
        t("token_bought_successfully"),
        "success"
      );
      clearInputs();
      fetchDetails();
      fetchUsdtBalance();
      fetchSaleTokenBalance();
    } catch (error) {
      Swal.fire(t("warning"), t("something_went_wrong"), "warning");
    } finally {
      setPurchaseLoading(false);
    }
  };
  const buyWithBNB = async () => {
    try {
      if (chainId !== bnbChainId) {
        Swal.fire(t("warning"), t("switch_chain_to_bsc"), "warning");
        return;
      }
      if (!isConnected) {
        Swal.fire(t("warning"), t("connect_wallet_first"), "warning");
        return;
      }
      let purchasableAmount = deci(availableForPurchase).div(10 ** 18);
      let receivableAmount = deci(amountTokenReceivedBnb);
      if (receivableAmount.greaterThan(purchasableAmount)) {
        Swal.fire(
          t("warning"),
          t("purchase_exceeds_available_tokens"),
          "warning"
        );
        return;
      }

      let weiValue = deci(amountBnbPay).mul(10 ** 18);
      if (weiValue.lessThan(0)) {
        Swal.fire(t("warning"), t("amount_greater_than_zero"), "warning");
        return;
      }
      if (deci(bnbBalance).lessThan(weiValue)) {
        Swal.fire(t("warning"), t("insufficient_balance"), "warning");
        return;
      }
      setPurchaseLoading(true);

      //Send Purchase Transaction
      let res = await presaleContract.buyWithBNB({
        value: weiValue.toFixed(0),
      });
      await res.wait();
      Swal.fire(
        t("transaction_completed"),
        t("token_bought_successfully"),
        "success"
      );
      clearInputs();
      fetchDetails();
      fetchUsdtBalance();
      fetchSaleTokenBalance();
    } catch (error) {
      console.log(error);
      Swal.fire(t("warning"), t("something_went_wrong"), "warning");
    } finally {
      setPurchaseLoading(false);
    }
  };

  const buyTokens = () => {
    if (bnbSelected) {
      buyWithBNB();
    } else {
      buyWithUSDT();
    }
    // Handle token purchase
    // Update state variables as needed
  };

  const updateProgressBar = () => {
    const currentTime = Math.floor(new Date().getTime() / 1000); // Current time in seconds
    let remainingTime = 0;
    let _saleState = getSaleState();
    if (_saleState === presaleStates.IN_FUTURE) {
      remainingTime = Math.floor(startDate.getTime() / 1000) - currentTime;
    } else if (_saleState === presaleStates.RUNNING) {
      remainingTime = Math.floor(endDate.getTime() / 1000) - currentTime;
    }
    console.log("sale state", _saleState, remainingTime, endDate);
    setSaleState(_saleState);
    if (remainingTime <= 0) {
      if (totalTime !== 0) {
        setTimeText(t("time_expired"));
      }
    } else {
      const days = Math.floor(remainingTime / (24 * 60 * 60));
      const hours = Math.floor((remainingTime % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((remainingTime % (60 * 60)) / 60);
      const seconds = Math.floor(remainingTime % 60);
      let res = `${days}d : ${hours}h : ${minutes}m : ${seconds}s`;
      setTimeText(res);
      setTimerData({
        days,
        minutes,
        hours,
        seconds,
      });
    }
  };

  // Function to start the countdown
  function startCountdown() {
    timerRef.current = setInterval(() => {
      updateProgressBar();
    }, 1000);
  }

  const watchAsset = async () => {
    if (!walletProvider) {
      Swal.fire(t("connect_wallet_first"), "", "warning");
      return;
    }
    try {
      await walletProvider.request({
        method: "wallet_watchAsset",
        params: {
          type: "ERC20", // The standard of the token (ERC20)
          options: {
            address: tokenAddress, // The contract address of the token
            symbol: tokenSymbol, // The symbol of the token
            decimals: token.decimals, // The number of decimals of the token
            image: token.image, // The image of the token
          },
        },
      });
      Swal.fire(t("token_imported"), "", "success");
    } catch (error) {
      Swal.fire(
        t("wallet_not_supported"),
        t("import_token_manually"),
        "warning"
      );
      console.log(error);
    }
  };
  useEffect(() => {
    console.log("rerender");
    clearInterval(timerRef.current);
    console.log("end date", endDate);
    startCountdown();
    return () => {
      clearInterval(timerRef.current);
    };
  }, [totalTime, startDate, endDate, tokensInPresale, totalTokensSold]);

  useEffect(() => {
    let cal = async () => {
      try {
        let amount = amountBnbPay.toString().trim();
        if (amount.length === 0) {
          setAmountTokenReceivedBnb("0.00");
          return;
        }
        let parsedAmount = deci(amount).mul(10 ** 18);
        if (!presaleContract) return;
        let numberOfToken = await presaleContract.bnbBuyHelper(
          parsedAmount.toFixed(0)
        );
        let amountReceivable = dividor(numberOfToken, 10 ** 18)
          .toFixed(2)
          .toString();
        setAmountTokenReceivedBnb(amountReceivable);
      } catch (error) {
        console.log(error);
      }
    };
    cal();
  }, [amountBnbPay, bnbSelected, presaleContract]);
  useEffect(() => {
    let cal = async () => {
      try {
        let amount = amountUsdtPay.toString().trim();
        if (amount.length === 0) {
          setAmountTokenReceivedUsdt("0.00");
          return;
        }
        amount = Big(amount.toString()) * Big(10 ** 9) * Big(10 ** 9);
        amount = Big(amount.toString());
        if (!presaleContract) return;
        let numberOfToken = await presaleContract.calculateToken(
          amount.toFixed(0).toString()
        );
        setAmountTokenReceivedUsdt(
          dividor(numberOfToken, 10 ** 18)
            .toFixed(2)
            .toString()
        );
      } catch (error) {
        console.log(error);
      }
    };
    cal();
  }, [amountUsdtPay, presaleContract]);

  useEffect(() => {
    if (!presaleContract) return;
    fetchDetails();
  }, [presaleContract]);

  useEffect(() => {
    fetchUsdtBalance();
  }, [tokenContract]);

  useEffect(() => {}, [saleTokenContract]);

  useEffect(() => {
    init();
  }, [chainId]);

  useEffect(() => {
    getInitialValues();
    fetchSaleTokenBalance();
  }, []);

  useEffect(() => {
    console.log("this", saleState);
    setSaleStateText(getSaleStateTextForTimer(saleState));
  }, [saleState]);
  return (
    <>
      <section className="wrapper">
        <div className="wrapper-head">
          <img src="./assets/Asset 1.png" alt="" />
        </div>
        <div className="wrapper-body">
          <div className="dateTime">
            <span id="days">
              <span>{timerData.days}</span>
              <br />
              <span className="formate">{t("Days")}</span>
            </span>
            <p>:</p>
            <span id="hours">
              <span>{timerData.hours}</span>
              <br />
              <span className="formate">{t("Hours")}</span>
            </span>
            <p>:</p>
            <span id="minutes">
              <span>{timerData.minutes}</span>
              <br />
              <span className="formate">{t("Mins")}</span>
            </span>
            <p>:</p>
            <span id="seconds">
              <span>{timerData.seconds}</span>
              <br />
              <span className="formate">{t("Sec")}</span>
            </span>
          </div>
          <div
            className="wrapper-title"
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <w3m-button
              label={t("connect_wallet")}
              class="btn-sky w-full p-0 flex justify-center items-center connect-btn"
            />
            {/* <h2>{saleStateText}</h2> */}
          </div>
          <div className="wrapper-content">
            <span className="text-sm">
              1 {token.symbol} = {tokenPrice}$
            </span>
            <div
              className="slider-wrapper"
              style={{
                margin: "5px 0px",
                display : 'none'
              }}
            >
              <div
                className="slider-track"
                style={{
                  width: soldPercent + "%",
                }}
              >
                <div className="slider-thumb">
                  <img
                    src="./assets/Asset 2.png"
                    alt="Pepe"
                    draggable="false"
                  />
                </div>
              </div>
            </div>
            <div className="stats">
              <div>
                <span>{t("TOKEN RAISED:")}</span>
                <span>
                  ${getFormattedAmount(totalTokensSold * tokenPrice)}
                </span>
              </div>
              <div>
                <span>{t("YOUR HOLDING:")} </span>
                <span>
                  {" "}
                  {formatNumber(fromWei(userBalance.tokenForSale))}{" "}
                  {token.symbol}
                </span>
              </div>
            </div>
            <hr className="dashed-line" />
            <p className="text-sm">{t("buy_select_label")}</p>
            <div className="flex-btns">
              <button
                onClick={() => {
                  setBnbSelected(true);
                }}
              >
                <img src="./assets/Asset 4.png" alt="bnb" /> <span>BNB</span>{" "}
              </button>
              <button
                onClick={() => {
                  setBnbSelected(false);
                }}
              >
                <img src="./assets/Asset 5.png" alt="usdt" /> <span>USDT</span>{" "}
              </button>
            </div>
            <form>
              <div className="form-wrapper">
                <label>
                  {" "}
                  {t("you_pay", { symbol: bnbSelected ? "BNB" : "USDT" })}
                </label>
                <div className="form-input">
                  <input
                    type="text"
                    value={bnbSelected ? amountBnbPay : amountUsdtPay}
                    placeholder={0}
                    required=""
                    onChange={(e) => {
                      if (bnbSelected) {
                        setAmountBnbPay(e.target.value);
                      } else {
                        setAmountUsdtPay(e.target.value);
                      }
                    }}
                  />
                  <div>
                    <button
                      type="button"
                      style={{
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => {
                        maxToken(bnbSelected);
                      }}
                    >
                      {t("MAX")}
                    </button>
                    <img
                      style={{
                        marginRight: "4px",
                      }}
                      src={`./assets/Asset ${bnbSelected ? "4" : "5"}.png`}
                      alt="eth"
                    />
                  </div>
                </div>
                <label>{t("you_receive", { symbol: token.symbol })}</label>
                <div className="form-input">
                  <input
                    value={formatNumber(
                      bnbSelected
                        ? amountTokenReceivedBnb
                        : amountTokenReceivedUsdt
                    )}
                    type="text"
                    placeholder={0}
                    readOnly
                  />
                </div>
                <div
                  className="btn mt-2"
                  style={{
                    padding: "0px",
                  }}
                >
                  {!isConnected && (
                    <div
                      className="btn btn-connect d-block w-100"
                      id="w3-button"
                      style={{
                        display: "flex !important",
                        justifyContent: "center",
                      }}
                    >
                      <w3m-button
                        label={t("connect_wallet")}
                        class="btn-sky w-full p-0 flex justify-center items-center connect-btn"
                      />
                    </div>
                  )}
                  {isConnected && (
                    <button
                      type="button"
                      id="buy-button"
                      onClick={
                        saleState === presaleStates.RUNNING ? buyTokens : null
                      }
                      className="btn d-block w-100 p-2"
                      style={{
                        backgroundColor: "#a0663a",
                        color: "white",
                        fontSize: "20px",
                        fontWeight: "700",
                      }}
                    >
                      <div
                        style={{
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 10,
                          height: "25px",
                          cursor: "pointer",
                          display: "flex",
                        }}
                      >
                        <div
                          style={{
                            display: purchaseLoading ? "inline-block" : "none",
                          }}
                          id="loader"
                          // className="btn-sky  w-full mt-3 py-4 text-xl"
                        />
                        {saleState === presaleStates.RUNNING
                          ? t("Buy Now")
                          : saleStateText}
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
        <div className="wrapper-footer">
          <span>{t("import_token", { symbol: token.symbol })}</span>
          <div className="btn">
            <button
              type="button"
              onClick={() => {
                watchAsset();
              }}
            >
              {t("+ ADD TOKEN")}
            </button>
          </div>
        </div>
        <div className="lastRock">
          <img src="./assets/Asset 6.png" alt="" />
        </div>
      </section>
    </>
  );
}

export default Swap;
